import { VEDIC_DIMENSIONS } from '../../lib/vedicDimensions';
import { SAMPRADAYA_OPTIONS } from '../../lib/sampradaya';
import { useGlossary } from '../../contexts/GlossaryContext';

export interface SevaFilter {
  dimension: string | null;
  mode: 'home' | 'remote' | 'either' | null;
  minAdhikara: number;
  sampradaya: string | null;
}

interface Props {
  filter: SevaFilter;
  onChange: (f: SevaFilter) => void;
}

export default function ServiceFilterBar({ filter, onChange }: Props) {
  const { t } = useGlossary();

  const set = (partial: Partial<SevaFilter>) =>
    onChange({ ...filter, ...partial });

  return (
    <aside className="seva-filter-bar" aria-label={t('seva.gigs.filter.label')}>
      {/* Service category chips */}
      <div className="seva-filter-bar__dims">
        <button
          className={`seva-dim-chip ${!filter.dimension ? 'seva-dim-chip--active' : ''}`}
          onClick={() => set({ dimension: null })}
        >
          ✦ {t('seva.gigs.filter.all')}
        </button>
        {VEDIC_DIMENSIONS.map((d) => (
          <button
            key={d.id}
            className={`seva-dim-chip ${filter.dimension === d.id ? 'seva-dim-chip--active' : ''}`}
            onClick={() => set({ dimension: filter.dimension === d.id ? null : d.id })}
            title={d.label}
          >
            {d.icon} {d.label}
          </button>
        ))}
      </div>

      {/* Mode toggle */}
      <div className="seva-filter-bar__modes">
        <span className="seva-filter-bar__label">{t('seva.gigs.filter.mode')}:</span>
        {(['home', 'remote', 'either'] as const).map((m) => (
          <button
            key={m}
            className={`seva-mode-btn ${filter.mode === m ? 'seva-mode-btn--active' : ''}`}
            onClick={() => set({ mode: filter.mode === m ? null : m })}
          >
            {m === 'home' ? `🏠 ${t('seva.gigs.mode.home')}`
              : m === 'remote' ? `💻 ${t('seva.gigs.mode.remote')}`
              : `🔄 ${t('seva.gigs.mode.either')}`}
          </button>
        ))}
      </div>

      {/* Adhikara minimum slider */}
      <div className="seva-filter-bar__adhikara">
        <label className="seva-filter-bar__label" htmlFor="seva-adhikara-min">
          {t('seva.gigs.filter.adhikara')}: {filter.minAdhikara}
        </label>
        <input
          id="seva-adhikara-min"
          type="range"
          min={0}
          max={9}
          value={filter.minAdhikara}
          onChange={(e) => set({ minAdhikara: Number(e.target.value) })}
          className="seva-filter-bar__slider"
        />
      </div>

      {/* Sampradaya / dharmic tradition chips */}
      <div className="seva-filter-bar__sampradaya">
        <span className="seva-filter-bar__label">{t('seva.gigs.filter.sampradaya')}:</span>
        <div className="seva-filter-bar__sampradaya-chips">
          <button
            className={`seva-dim-chip ${!filter.sampradaya ? 'seva-dim-chip--active' : ''}`}
            onClick={() => set({ sampradaya: null })}
          >
            ✦ {t('seva.gigs.sampradaya.any')}
          </button>
          {SAMPRADAYA_OPTIONS.map((s) => (
            <button
              key={s.id}
              className={`seva-dim-chip ${filter.sampradaya === s.id ? 'seva-dim-chip--active' : ''}`}
              onClick={() => set({ sampradaya: filter.sampradaya === s.id ? null : s.id })}
              title={t(s.glossaryKey)}
            >
              {s.icon} {t(s.glossaryKey)}
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
}
